
import React from 'react';
import { ArrowLeft, ShieldCheck, Scroll, Eye, Gavel, Scale } from 'lucide-react';

interface LegalPageProps {
  onBack: () => void;
}

export const PrivacyPolicy: React.FC<LegalPageProps> = ({ onBack }) => (
  <div className="min-h-screen bg-slate-950 pt-32 pb-24 px-4 animate-fade-in relative overflow-hidden">
    <div className="absolute top-0 right-0 p-24 opacity-5 pointer-events-none">
       <Eye size={400} className="text-amber-500" />
    </div>
    
    <div className="max-w-4xl mx-auto relative z-10">
      <button onClick={onBack} className="flex items-center gap-2 text-amber-500 text-[10px] uppercase font-bold tracking-widest mb-12 hover:text-white transition-colors">
        <ArrowLeft size={14} /> Back to Sanctuary
      </button>
      
      <div className="flex items-center gap-4 mb-6">
        <ShieldCheck className="text-amber-500" size={32} />
        <h1 className="text-4xl md:text-6xl font-serif text-white uppercase tracking-widest">Privacy Policy</h1>
      </div>
      
      <p className="text-indigo-400 text-xs uppercase tracking-[0.4em] font-bold mb-12 border-b border-indigo-900/30 pb-6">Celestial Data Governance • Effective Jan 2024</p>
      
      <div className="space-y-12 text-indigo-100/70 leading-relaxed font-light">
        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">1. The Nature of Soul Data</h3>
          <p>When you consult the heavens with Allie, you provide sensitive birth details: Date, Time, and Location. We treat this as "Sacred Data." This information is used exclusively to generate your natal alignments and personalized horoscopes.</p>
        </section>

        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">2. Celestial Encryption</h3>
          <p>Your birth coordinates and membership records are stored using industry-standard AES-256 encryption. We do not sell soul insights to third-party entities. Your cosmic blueprint remains your own.</p>
        </section>

        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">3. Payment & Treasury</h3>
          <p>All financial exchanges are handled via PayPal's secure gateway. Allie never sees or stores your credit card or bank account details. The treasury is separate from the sanctuary.</p>
        </section>

        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">4. Your Right to Erasure</h3>
          <p>You may request the full deletion of your registry entry at any time. Upon request, all birth charts, messages, and fated points will be wiped from the celestial database within 48 hours.</p>
        </section>
      </div>
    </div>
  </div>
);

export const TermsOfService: React.FC<LegalPageProps> = ({ onBack }) => (
  <div className="min-h-screen bg-slate-950 pt-32 pb-24 px-4 animate-fade-in relative overflow-hidden">
    <div className="absolute bottom-0 left-0 p-24 opacity-5 pointer-events-none">
       <Scale size={400} className="text-indigo-500" />
    </div>
    
    <div className="max-w-4xl mx-auto relative z-10">
      <button onClick={onBack} className="flex items-center gap-2 text-amber-500 text-[10px] uppercase font-bold tracking-widest mb-12 hover:text-white transition-colors">
        <ArrowLeft size={14} /> Back to Sanctuary
      </button>
      
      <div className="flex items-center gap-4 mb-6">
        <Scroll className="text-amber-500" size={32} />
        <h1 className="text-4xl md:text-6xl font-serif text-white uppercase tracking-widest">Terms of Service</h1>
      </div>
      
      <p className="text-indigo-400 text-xs uppercase tracking-[0.4em] font-bold mb-12 border-b border-indigo-900/30 pb-6">Sacred Agreement • Effective Jan 2024</p>
      
      <div className="space-y-12 text-indigo-100/70 leading-relaxed font-light">
        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">1. Ethical Boundaries</h3>
          <p>By using this site, you acknowledge that astrology is a symbolic language of interpretation. Allie does not provide medical diagnosis, legal counsel, or financial investment advice. Seekers are responsible for their own earthly actions.</p>
        </section>

        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">2. Exchange Policy</h3>
          <p>Booking a session requires a secure upfront exchange. Due to the deep preparation required for precision chart mapping, refunds are not offered for completed readings. Rescheduling requires 24-hour celestial notice.</p>
        </section>

        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">3. Membership Circles</h3>
          <p>Membership tiers (Lunar, Solar, Galactic) are subscription-based. Benefits must be utilized within the monthly cycle and do not carry over unless specified in a custom alignment agreement.</p>
        </section>

        <section className="space-y-4">
          <h3 className="text-xl font-serif text-white uppercase tracking-widest">4. Conduct in the Sanctuary</h3>
          <p>Abusive behavior toward Allie or other seekers in community spaces will result in immediate exile from the sanctuary and permanent termination of registry access without refund.</p>
        </section>
      </div>
    </div>
  </div>
);
